import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useBulkCollectionsFeature, useBulkCollectionsFeatureSettings, usePaymentRole } from '@frontend/payments-hooks';
import { useMerchants } from '@frontend/payments-hooks';
import { useStripeOnboarding } from '@frontend/payments-setup';
import { useBulkCollectionsUIShallowStore } from '@frontend/payments-state';
import { useAppScopeStore } from '@frontend/scope';
import { useEligibility, usePreference } from '@frontend/user-preferences';
import { useModalControl } from '@frontend/design-system';
import { BulkCollectionsBannerType, getPmsDisplayName } from '../constants';
import { BulkCollectionsPromoTrackingIds } from '../tracking-ids';

/**
 * Determines eligibility and dismissal state for the Bulk Collections PLG banner.
 *
 * Combines:
 * - PMS eligibility (source-info + feature-settings via useBulkCollectionsFeature)
 * - Stripe account status (useMerchants) → selects which banner variant to show
 * - Role check (usePaymentRole) → Super Admin, Billing Manager, Payments User
 * - Dismissal strategies:
 *   - Permanent dismissal (disableEligibility): "Start Collecting", "Enable Collections", callout dismiss buttons
 *   - Session dismissal (dismissForSession): "Maybe Later" button - reappears in next session
 */
export const useBulkCollectionsBannerEligibility = ({
  navigateToCollections,
}: {
  navigateToCollections: () => void;
}) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation('highValueAdoption');
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  const { resetSelectAllCallout } = useBulkCollectionsUIShallowStore('resetSelectAllCallout');

  // Wrap navigateToCollections to reset the callout
  const navigateToCollectionsWithReset = useCallback(() => {
    resetSelectAllCallout();
    navigateToCollections();
  }, [resetSelectAllCallout, navigateToCollections]);

  // PMS eligibility: supported PMS + bulk collections capability enabled
  const { pmsName, featureSettingEnabled, isBulkCollectionsEligible, enableCollectionFlow } =
    useBulkCollectionsFeature();

  const { toggleFeatureSetting } = useBulkCollectionsFeatureSettings(pmsName ?? '', setIsButtonLoading, {
    onSuccess: () => {
      setTimeout(() => {
        navigateToCollectionsWithReset();
        setIsButtonLoading(false);
      }, 1000);
    },
  });

  // Stripe/merchant status — only fetch once PMS eligibility is confirmed
  const {
    anyLocationHasPayments,
    firstLocation: { activeProcessor, locationId, refetch },
  } = useMerchants('selectedLocationIds', {
    enabled: isBulkCollectionsEligible,
  });

  const { modalProps, triggerProps } = useModalControl();
  const { onCreateAccountClick, onContinueOnboardClick } = useStripeOnboarding({
    locationId: locationId || '',
    onCreateError: modalProps.onClose,
    onRedirect: modalProps.onClose,
    refetch,
  });

  // Role gate: Super Admin, Billing Manager, Payments User
  const { hasAnalyticsRole } = usePaymentRole(selectedLocationIds, 'any');

  // Server-synced banner dismissal - disableEligibility for permanent dismissal
  const { shouldShow: shouldShowBanner, disableEligibility } = useEligibility({
    featureId: 'bulk-collections-banner',
    defaultCriteria: {
      type: 'dismiss-cooldown',
      maxDismisses: 99999,
      cooldownDays: 1, // Required but unused (using sessionStorage for temporary dismissal)
    },
  });

  // Session-only dismissal state for "Maybe Later" button
  const [isSessionDismissed, setIsSessionDismissed] = useState(() => {
    return sessionStorage.getItem('bulk-collections-banner-session-dismissed') === 'true';
  });

  const dismissForSession = useCallback(() => {
    sessionStorage.setItem('bulk-collections-banner-session-dismissed', 'true');
    setIsSessionDismissed(true);
  }, []);

  // Access Eligibility preferences to enable banner revocation (for testing)
  const [eligibility, setEligibility] = usePreference('Eligibility');

  // Function to revoke/clear banner dismissal (useful for testing)
  const revokeBanner = useCallback(() => {
    if (eligibility) {
      const { 'bulk-collections-banner': _, ...rest } = eligibility;
      setEligibility(rest);
    }
    sessionStorage.removeItem('bulk-collections-banner-session-dismissed');
    setIsSessionDismissed(false);
  }, [eligibility, setEligibility]);
  // window.revokeBanner = revokeBanner; // Expose for testing

  const showBanner = isBulkCollectionsEligible && hasAnalyticsRole && shouldShowBanner && !isSessionDismissed;

  const { bannerType, primaryAction } = useMemo(() => {
    const primaryActionMap = {
      [BulkCollectionsBannerType.NoPayments]: {
        text: activeProcessor?.stripeId ? t('Continue Onboarding') : t('Activate Weave Payments'),
        trackingId: BulkCollectionsPromoTrackingIds.activateWeavePayments,
        onClick: () => {
          if (activeProcessor?.stripeId) {
            onContinueOnboardClick({
              stripeId: activeProcessor.stripeId,
              setLoading: setIsButtonLoading,
            });
          } else {
            triggerProps.onClick();
          }
        },
      },
      [BulkCollectionsBannerType.CollectionsFeatureDisabled]: {
        text: t('Enable Collections'),
        trackingId: BulkCollectionsPromoTrackingIds.enableCollections,
        onClick: () => {
          setIsButtonLoading(true);
          disableEligibility(); // Dismiss banner permanently when enabling collections
          toggleFeatureSetting(true);
        },
      },
      [BulkCollectionsBannerType.CollectionsFeatureEnabled]: {
        text: t('Start Collecting'),
        trackingId: BulkCollectionsPromoTrackingIds.startCollecting,
        onClick: () => {
          disableEligibility(); // Dismiss banner permanently when starting to collect
          navigateToCollectionsWithReset();
        },
      },
    };

    if (!showBanner)
      return {
        bannerType: BulkCollectionsBannerType.NoPayments,
        primaryAction: primaryActionMap[BulkCollectionsBannerType.NoPayments],
      };

    if (!anyLocationHasPayments) {
      return {
        bannerType: BulkCollectionsBannerType.NoPayments,
        primaryAction: primaryActionMap[BulkCollectionsBannerType.NoPayments],
      };
    } else if (!featureSettingEnabled || isButtonLoading) {
      return {
        bannerType: BulkCollectionsBannerType.CollectionsFeatureDisabled,
        primaryAction: primaryActionMap[BulkCollectionsBannerType.CollectionsFeatureDisabled],
      };
    } else {
      return {
        bannerType: BulkCollectionsBannerType.CollectionsFeatureEnabled,
        primaryAction: primaryActionMap[BulkCollectionsBannerType.CollectionsFeatureEnabled],
      };
    }
  }, [showBanner, anyLocationHasPayments, featureSettingEnabled, activeProcessor, isButtonLoading]);

  return {
    showBanner,
    hasStripeAccount: !!anyLocationHasPayments,
    markDismissed: dismissForSession, // Session-only dismissal for "Maybe Later" - reappears next session
    dismissPermanently: disableEligibility, // Permanent dismissal for callouts and primary actions
    revokeBanner, // For testing: clears banner dismissal state (both permanent and session)
    bannerType: bannerType as BulkCollectionsBannerType,
    pmsName: getPmsDisplayName(pmsName ?? ''),
    primaryAction,
    stripeSignupModalProps: {
      modalProps,
      onCreateAccount: onCreateAccountClick,
    },
    isButtonLoading,
    enableCollectionFlow,
  };
};
